import { createContext, useContext, useState, ReactNode, Dispatch, SetStateAction } from 'react';
import { OpportunityTableFilters, SortingOption, Pagination, Opportunity } from './opportunity';
import useOpportunityStatuses from "@/opportunity/useOpportunityStatuses";
import StaffOnly from "@/user/StaffOnly";
import ChangeStatusModal from "@/opportunity/ChangeStatusModal";
import { useRouter } from '@tanstack/react-router'
import { useUser } from "@/api/api";

export enum OpportunityManagementView {
  TABLE = "TABLE",
  BOARD = "BOARD",
}

interface OpportunityManagementContextType {
  filters: OpportunityTableFilters;
  setFilters: Dispatch<SetStateAction<OpportunityTableFilters>>;
  sorting: SortingOption[];
  setSorting: Dispatch<SetStateAction<SortingOption[]>>;
  pagination: Pagination;
  setPagination: Dispatch<SetStateAction<Pagination>>;
  openOpportunity: (opportunity: Opportunity) => void;
  openChangeOpportunityStatusModal: (opportunity: Opportunity) => void;
  view: OpportunityManagementView;
  setView: Dispatch<SetStateAction<OpportunityManagementView>>;
}

const OpportunityManagementContext = createContext<OpportunityManagementContextType | undefined>(undefined);

export const OpportunityManagementContextProvider = ({ children, organizationId }: {
  children: ReactNode,
  organizationId?: number
}) => {

  const [view, setView] = useState<OpportunityManagementView>(OpportunityManagementView.BOARD);

  const { data: defaultStatusOptions } = useOpportunityStatuses();

  const { data: user } = useUser();

  const defaultFilters: OpportunityTableFilters = {
    location: "",
    category: "",
    organization_id: organizationId,
    status: user?.is_staff
      ? defaultStatusOptions.map((status) => status.name).filter((status) => !status.includes("Closed"))     // Exclude "Closed" opportunities by default on component mount
      : defaultStatusOptions.map((status) => status.name)
  };

  const [filters, setFilters] = useState<OpportunityTableFilters>(defaultFilters);

  const defaultSorting: SortingOption[] = [{ id: "category__severity", desc: true }];
  const [sorting, setSorting] = useState<SortingOption[]>(defaultSorting);

  const defaultPagination: Pagination = {
    pageIndex: 0,
    pageSize: 10,
  };

  const [pagination, setPagination] = useState<Pagination>(defaultPagination);

  const [changeStatusModalOpen, setChangeStatusModalOpen] = useState(false)
  const [changeStatusModalSelectedOpportunity, setChangeStatusModalSelectedOpportunity] = useState<Opportunity | undefined>(undefined)

  const router = useRouter();

  const openOpportunity = (opportunity: Opportunity) => {
    const path = router.buildLocation({
      to: "/organization/$organizationId/issues/$opportunityId",
      params: {
        opportunityId: opportunity.id,
        organizationId: opportunity.organization_id
      }
    }).href
    window.open(path, '_blank');
  }

  const openChangeOpportunityStatusModal = (opportunity: Opportunity) => {
    setChangeStatusModalSelectedOpportunity(opportunity);
    setChangeStatusModalOpen(true);
  }

  return (
    <OpportunityManagementContext.Provider
      value={{
        filters: {
          ...filters,
          ...(organizationId && { organization_id: organizationId })
        },
        setFilters,
        sorting,
        setSorting,
        pagination,
        setPagination,
        openOpportunity,
        openChangeOpportunityStatusModal,
        view,
        setView,
      }}>
      {children}
      <StaffOnly>
        {
          changeStatusModalSelectedOpportunity && (
            <ChangeStatusModal
              isOpen={changeStatusModalOpen}
              onClose={() => setChangeStatusModalOpen(false)}
              opportunity={changeStatusModalSelectedOpportunity}
            />
          )
        }
      </StaffOnly>
    </OpportunityManagementContext.Provider>
  );
};


export const useOpportunityManagement = () => {
  const context = useContext(OpportunityManagementContext);
  if (context === undefined) {
    throw new Error("OpportunityManagementContext must be used within its Provider");
  }
  return context;
};